import { useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { useEntityDetail } from '@/hooks/useDashboard';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/dashboard/DashboardCards';
import { routes } from '@/routes/paths';
import type { EntityDetailType } from '@/types/entityDetail';

export type EntityModalRef = {
  type: EntityDetailType;
  id: number | string;
  title?: string;
};

/** SPA destinations for entity types (Laravel action URLs are not SPA routes). */
function spaPrimaryAction(type: string, id: number | string): { to: string; label: string } | null {
  switch (type) {
    case 'restaurant':
      return { to: `${routes.stores}/${id}/detail`, label: 'View store' };
    case 'food':
      return { to: `${routes.items}/${id}`, label: 'View item' };
    case 'customer':
      return { to: `${routes.customers}/${id}`, label: 'View customer' };
    case 'deliveryman':
      return { to: `${routes.deliverymen}/${id}/detail`, label: 'View delivery man' };
    case 'order':
      return { to: `${routes.orders}/${id}`, label: 'View order' };
    default:
      return null;
  }
}

function spaListLink(id: number | string, listTitle: string, title: string) {
  const t = listTitle.toLowerCase();
  if (t.includes('food') || t.includes('item')) {
    if (title.startsWith('#')) return `${routes.items}/${id}`;
    return routes.items;
  }
  if (t.includes('order') || t.includes('deliver')) {
    return `${routes.orders}/${id}`;
  }
  return null;
}

export function EntityDetailModal({
  target,
  onClose,
}: {
  target: EntityModalRef | null;
  onClose: () => void;
}) {
  const type = target?.type ?? null;
  const id = target?.id ?? null;
  const query = useEntityDetail(type, id);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!target) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [target, onClose]);

  const detail = query.data;
  const title = detail?.name || target?.title || 'Details';
  const primary =
    detail && spaPrimaryAction(String(detail.type), detail.id);

  return (
    <Modal
      open={!!target}
      title={`${title}`}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {primary ? (
            <Link
              to={primary.to as never}
              className="btn btn-primary btn-sm"
              onClick={onClose}
            >
              {primary.label}
            </Link>
          ) : null}
        </>
      }
    >
      <div ref={panelRef} className="entity-modal">
        {query.isLoading ? (
          <>
            <Skeleton height={88} />
            <Skeleton height={72} />
            <Skeleton height={120} />
          </>
        ) : query.error ? (
          <div className="rank-error" role="alert">
            <p>Unable to load details.</p>
            <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </div>
        ) : detail ? (
          <>
            <header className="entity-modal-header">
              {detail.image ? (
                <img src={detail.image} alt="" width={64} height={64} className="entity-modal-image" />
              ) : (
                <span className="entity-modal-image entity-modal-fallback" aria-hidden>
                  {String(detail.name || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
              <div>
                <h3 className="entity-modal-name">{detail.name}</h3>
                {detail.subtitle ? <p className="muted">{detail.subtitle}</p> : null}
                <div className="entity-modal-badges">
                  {detail.status_label ? (
                    <span className={`status-badge status-${detail.status_label}`}>{detail.status_label}</span>
                  ) : null}
                  {detail.category ? <span className="chip">{detail.category}</span> : null}
                  {detail.rating != null && detail.rating !== '' ? (
                    <span className="chip">★ {String(detail.rating)}</span>
                  ) : null}
                </div>
              </div>
            </header>

            {detail.metrics?.length ? (
              <div className="entity-metrics">
                {detail.metrics.map((m) => (
                  <div key={m.key} className="entity-metric">
                    <div className="metric-label">{m.label}</div>
                    <div className="entity-metric-value">{m.value}</div>
                  </div>
                ))}
              </div>
            ) : null}

            {detail.info && Object.keys(detail.info).length ? (
              <section>
                <h4 className="entity-section-title">Info</h4>
                <dl className="entity-info">
                  {Object.entries(detail.info).map(([k, v]) => (
                    <div key={k} className="entity-info-row">
                      <dt className="muted">{k}</dt>
                      <dd>{v == null || v === '' ? '—' : String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}

            {detail.lists && Object.keys(detail.lists).length
              ? Object.entries(detail.lists).map(([listTitle, rows]) => {
                  if (!rows?.length) return null;
                  return (
                    <section key={listTitle}>
                      <h4 className="entity-section-title">{listTitle}</h4>
                      <ul className="entity-list">
                        {rows.map((row, i) => {
                          const href = spaListLink(detail.id, listTitle, row.title);
                          const body = (
                            <>
                              <strong>{row.title}</strong>
                              {row.meta ? <span className="muted">{row.meta}</span> : null}
                            </>
                          );
                          return (
                            <li key={`${listTitle}-${i}`}>
                              {href ? (
                                <Link to={href as never} className="entity-list-row" onClick={onClose}>
                                  {body}
                                </Link>
                              ) : (
                                <div className="entity-list-row">{body}</div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  );
                })
              : null}
          </>
        ) : (
          <p className="muted">No details found.</p>
        )}
      </div>
    </Modal>
  );
}
